import { createContext, useContext } from "react";
import { DEFAULT_LOCALE, dirFor, type Locale } from "./config";
import { getDictionary, type Dictionary } from "./dictionaries";

interface LocaleContextValue {
  locale: Locale;
  dir: "ltr" | "rtl";
  dict: Dictionary;
  setLocale?: (locale: Locale) => void | Promise<void>;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  dir: "ltr",
  dict: getDictionary(DEFAULT_LOCALE),
});

export function LocaleProvider({
  locale,
  onLocaleChange,
  children,
}: {
  locale: Locale;
  onLocaleChange?: (locale: Locale) => void | Promise<void>;
  children: React.ReactNode;
}) {
  const value: LocaleContextValue = {
    locale,
    dir: dirFor(locale),
    dict: getDictionary(locale),
    setLocale: onLocaleChange,
  };
  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}

export function useDict(): Dictionary {
  return useContext(LocaleContext).dict;
}

export function fill(
  template: string,
  vars: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    k in vars ? String(vars[k]) : `{${k}}`,
  );
}
